import { Col, Input, Row, Typography, Tooltip, Spin, Empty } from 'antd';
import ChargerMap from './ChargerMap';
import { MapErrorBoundary } from './ErrorBoundary';
const { Text, Title } = Typography;
import { SearchOutlined } from '@ant-design/icons';
import { useFetchChargerList } from '../Pages/chargers/Hooks';
import { useState, useRef, useEffect } from 'react';
import { CSS2, Default, GBT, Type2 } from './Icons';
import { useTranslation } from 'react-i18next';

const statusColors = {
  ONLINE: 'green',
  OFFLINE: 'red',
};

const ChargerDashboard = () => {
  const { t } = useTranslation('dashboard');
  const [search, setSearch] = useState('');
  const [selectedChargerId, setSelectedChargerId] = useState(null);
  const listRef = useRef(null);
  const [listHeight, setListHeight] = useState(500);
  const limit = 5;

  useEffect(() => {
    const updateListHeight = () => {
      const windowHeight = window.innerHeight;
      setListHeight(Math.min(500, windowHeight * 0.6));
    };

    updateListHeight();
    window.addEventListener('resize', updateListHeight);
    return () => window.removeEventListener('resize', updateListHeight);
  }, []);

  const { data: chargerData, isLoading } = useFetchChargerList({
    limit,
    search,
    offset: 0,
  });

  const chargers = chargerData?.data?.paginatedData || [];

  const handleChargerSelect = (chargerId) => {
    setSelectedChargerId(chargerId);
    // Scroll to the selected charger if it's not in view
    if (chargerId) {
      const selectedElement = document.getElementById(`charger-${chargerId}`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const getConnectorImage = (connector) => {
    const connectorImages = {
      TYPE2: <Type2 className="size-8" status={connector.status} />,
      CCS2: <CSS2 className="size-8" status={connector.status} />,
      GBT: <GBT className="size-6" status={connector.status} />,
    };

    return (
      connectorImages[connector.connectorType] || (
        <Default className="size-6" status={connector.status} />
      )
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <Title level={5} className="mb-4! text-sm font-semibold">
        {t('dashboard:chargerMapView')}
      </Title>
      <Row gutter={[16, 16]} className="min-h-[400px]">
        {/* LEFT - Charger List */}
        <Col xs={24} sm={24} md={8} className="flex flex-col">
          <Input
            allowClear
            placeholder={t('dashboard:searchChargers')}
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-4"
          />
          <div 
            ref={listRef}
            className="flex-grow overflow-y-auto pr-2"
            style={{ maxHeight: listHeight }}
          >
            {isLoading ? (
              <div className="flex justify-center items-center h-full">
                <Spin />
              </div>
            ) : chargers.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('dashboard:noChargersFound')}
              />
            ) : (
              chargers.map((charger) => (
                <div
                  key={charger._id}
                  id={`charger-${charger._id}`}
                  onClick={() => handleChargerSelect(charger._id)}
                  className={`
                    mb-4 cursor-pointer transition-all duration-200
                    hover:shadow-md
                  `}
                >
                  <div
                    className={`
                      shadow-sm border rounded-xl p-3 bg-white
                      ${selectedChargerId === charger._id ? 'border-green-700 bg-green-50/10' : 'border-gray-200'}
                      transition-all duration-200
                    `}
                  >
                    <div className="space-y-3">
                      {/* Header Section */}
                      <div className="flex justify-between items-start gap-2">
                        <div className="space-y-1 min-w-0 flex-1">
                          <Text strong className="text-base block truncate">
                            {charger.name}
                          </Text>
                          <Text type="secondary" className="text-xs block">
                            {charger.serialNumber}
                          </Text>
                        </div>
                        <div
                          className={`
                            flex items-center gap-1 px-3 py-0.5 rounded-md text-xs font-medium whitespace-nowrap
                            ${
                              statusColors[charger.status] === 'green'
                                ? 'bg-green-50 text-green-600 border border-green-200'
                                : statusColors[charger.status] === 'red'
                                  ? 'bg-red-50 text-red-600 border border-red-200'
                                  : 'bg-gray-50 text-gray-600 border border-gray-200'
                            }
                          `}
                        >
                          <div
                            className={`
                              w-1.5 h-1.5 rounded-full
                              ${
                                statusColors[charger.status] === 'green'
                                  ? 'bg-green-600'
                                  : statusColors[charger.status] === 'red'
                                    ? 'bg-red-600'
                                    : 'bg-gray-600'
                              }
                            `}
                          />
                          {t(`dashboard:chargerStatus.${charger.status}`, { defaultValue: charger.status })}
                        </div>
                      </div>

                      {/* Location */}
                      <div className="flex items-center text-gray-500 text-sm">
                        <svg
                          className="w-4 h-4 mr-1 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="truncate">{charger.locationData[0]?.locationName}</span>
                      </div>

                      {/* Connectors Section */}
                      {charger.connectors && (
                        <div className="space-y-1">
                          <Text type="secondary" className="text-xs block">
                            {t('dashboard:connectors')}:
                          </Text>
                          <div className="flex flex-wrap gap-2">
                            {charger.connectors.map((connector) => (
                              <Tooltip
                                key={connector._id}
                                title={
                                  <div className="space-y-1">
                                    <div className="font-medium">
                                      {t('dashboard:connectorDetails')}
                                    </div>
                                    <div>
                                      {t('dashboard:type')}: {connector.connectorType}
                                    </div>
                                    <div>ID: {connector.connectorId}</div>
                                    <div>
                                      {t('dashboard:status')}:{' '}
                                      {connector.status === 'Ready'
                                        ? '-'
                                        : connector.status}
                                    </div>
                                  </div>
                                }
                                placement="top"
                              >
                                <div className="flex items-center p-2 rounded-md hover:bg-gray-50 transition-colors">
                                  {getConnectorImage(connector)}
                                </div>
                              </Tooltip>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Col>

        {/* RIGHT - Map */}
        <Col xs={24} sm={24} md={16} className="h-[500px]">
          <div className="h-full min-h-[500px] relative overflow-hidden shadow-md">
            <MapErrorBoundary>
              <ChargerMap
                chargers={chargers}
                selectedChargerId={selectedChargerId}
                onChargerSelect={handleChargerSelect}
              />
            </MapErrorBoundary>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ChargerDashboard;
